import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SearchOutlined } from "@ant-design/icons";
import { Input } from "antd";
import DefaultViewHeader from "../../components/main/default_view_header/DefaultViewHeader";
import AuthContext from "../../contexts/AuthContext";
import CreateHomeModal from "../../components/home/create_home_modal/CreateHomeModal";
import CreateDeviceModal from "../../components/home/create_device_modal/CreateDeviceModal";
import { connect } from "react-redux";
import { mapDispatchToProps, mapStateToProps } from "../../store/dispatcher";
import { DispatchProps, StateProps } from "../../store";
import HomeItem from "../../components/home/home_item/HomeItem";
import "./style.scss";
import { Device } from "../../lib/Device";
import { Home as HomeType } from "../../lib/Home";
import { ReactComponent as NewHome } from "../../assets/icons/new-home.svg";
import { homeMatchesSearchQuery } from "../../utils/search/homeSearch";

const Home = ({
  homes,
  devices,
  fetchDevices,
  fetchHomes,
}: {
  homes: HomeType[];
  devices: Device[];
  fetchDevices: any;
  fetchHomes: any;
}) => {
  const { t } = useTranslation("home");
  const { user } = useContext(AuthContext);
  const isBasicUser = !user.is_installer && !user.is_advanced_user;
  const [createHome, setCreateHome] = useState(false);
  const [createDevice, setCreateDevice] = useState(false);
  const [homeSearchQuery, setHomeSearchQuery] = useState("");

  const selected_home = useRef(-1);

  // Restore own fleet after browsing a subordinate fleet via Partners
  useEffect(() => {
    if (!user.is_installer) return;
    fetchHomes(undefined);
    fetchDevices(undefined);
  }, [user.is_installer, fetchHomes, fetchDevices]);

  const filteredHomes = useMemo(() => {
    if (!homes?.length) return [];
    return homes.filter((h) =>
      homeMatchesSearchQuery(h, devices ?? null, homeSearchQuery)
    );
  }, [homes, devices, homeSearchQuery]);

  return (
    <div id="home-view">
      <CreateHomeModal open={createHome} close={() => setCreateHome(false)} />
      <CreateDeviceModal
        open={createDevice}
        device={selected_home.current}
        close={() => setCreateDevice(false)}
      />
      <DefaultViewHeader
        title={user.is_installer ? 
          `${t("installer_dashboard_title")} - <span>${user.first_name}</span>` : 
          `${t("main:hello")} <span>${user.first_name}</span>,`
        }
        description={user.is_installer ? 
          t("installer_dashboard_description") : 
          t("home_description")
        }
        big_title={true}
        action_title={t("new_home")}
        onClick={
          !isBasicUser && homes && homes.length !== 0
            ? () => setCreateHome(true)
            : undefined
        }
      />
      {user.is_installer && homes && homes.length > 0 && (
        <div className="installer-dashboard-strip">
          <div className="installer-dashboard-strip__stats">
            <div>
              <strong style={{ fontSize: '18px', color: '#1976d2' }}>
                {(() => {
                  const uniqueCustomers = new Set();
                  homes.forEach((home: HomeType) => {
                    if (home.user_email) {
                      uniqueCustomers.add(`${home.user_email}-${home.user_first_name}-${home.user_last_name}`);
                    }
                  });
                  return uniqueCustomers.size;
                })()}
              </strong>
              <span style={{ marginLeft: '8px', color: '#666' }}>{t("customers_label")}</span>
            </div>
            <div>
              <strong style={{ fontSize: '18px', color: '#1976d2' }}>
                {homes.length}
              </strong>
              <span style={{ marginLeft: '8px', color: '#666' }}>{t("homes_label")}</span>
            </div>
            <div>
              <strong style={{ fontSize: '18px', color: '#1976d2' }}>
                {devices ? devices.length : 0}
              </strong>
              <span style={{ marginLeft: '8px', color: '#666' }}>{t("devices_label")}</span>
            </div>
          </div>
          <div className="installer-dashboard-strip__search">
            <Input
              allowClear
              value={homeSearchQuery}
              onChange={(e) => setHomeSearchQuery(e.target.value)}
              placeholder={t("search_homes_placeholder")}
              className="home-search-input"
              prefix={<SearchOutlined className="home-search-prefix-icon" />}
            />
          </div>
        </div>
      )}
      {homes && homes.length > 0 && !user.is_installer && (
        <div className="home-search-row">
          <Input
            allowClear
            value={homeSearchQuery}
            onChange={(e) => setHomeSearchQuery(e.target.value)}
            placeholder={t("search_homes_placeholder")}
            className="home-search-input"
            prefix={<SearchOutlined className="home-search-prefix-icon" />}
          />
        </div>
      )}
      <div className="homes-container">
        {homes && homes.length === 0 && !user.is_installer && (!isBasicUser || homes.length === 0) && (
          <div className="empty-home" onClick={() => setCreateHome(true)}>
            <NewHome />
            <span>{t("new_home_modal_title")}</span>
          </div>
        )}
         {homes && homes.length === 0 && user.is_installer && (
          <div className="empty-home" onClick={() => setCreateHome(true)}>
            <NewHome />
            <span>{t("no_homes_assigned")}</span>
          </div>
        )}
        {homes &&
          homes.length > 0 &&
          filteredHomes.length === 0 &&
          homeSearchQuery.trim() !== "" && (
            <div className="home-search-empty">{t("search_homes_no_results")}</div>
          )}
        {user.is_installer ? (
          // Group homes by customer for installers
          (() => {
            const groupedHomes = filteredHomes.reduce((acc: any, home: HomeType) => {
              const customerKey = `${home.user_email}-${home.user_first_name}-${home.user_last_name}`;
              if (!acc[customerKey]) {
                acc[customerKey] = {
                  customer: {
                    email: home.user_email,
                    first_name: home.user_first_name,
                    last_name: home.user_last_name
                  },
                  homes: []
                };
              }
              acc[customerKey].homes.push(home);
              return acc;
            }, {});

            return Object.values(groupedHomes).map((group: any, groupIndex: number) => (
              <div key={groupIndex} style={{ marginBottom: '24px' }}>
                {user.show_customer_identity && (
                  <div style={{ 
                    padding: '12px 16px', 
                    backgroundColor: '#e3f2fd', 
                    borderRadius: '8px',
                    marginBottom: '12px',
                    border: '1px solid #bbdefb'
                  }}>
                    <h3 style={{ 
                      margin: 0, 
                      fontSize: '16px', 
                      fontWeight: '600',
                      color: '#1976d2'
                    }}>
                      {group.customer.first_name} {group.customer.last_name}
                    </h3>
                    <p style={{ 
                      margin: '4px 0 0 0', 
                      fontSize: '14px', 
                      color: '#666' 
                    }}>
                      {group.customer.email}
                    </p>
                  </div>
                )}
                {group.homes.map((home: HomeType, homeIndex: number) => (
                  <div key={homeIndex} style={{ marginBottom: '12px' }}>
                    <HomeItem
                      home={home}
                      new_device={(home_id: number) => {
                        selected_home.current = home_id;
                        setCreateDevice(true);
                      }}
                      devices={devices ? devices.filter((d) => d.home === home.id) : []}
                    />
                  </div>
                ))}
              </div>
            ));
          })()
        ) : (
          // Regular users see homes as before
          filteredHomes.map((home: HomeType, index: number) => (
            <HomeItem
              key={index}
              home={home}
              new_device={(home_id: number) => {
                selected_home.current = home_id;
                setCreateDevice(true);
              }}
              devices={devices ? devices.filter((d) => d.home === home.id) : []}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default connect<StateProps, DispatchProps>(
  mapStateToProps,
  mapDispatchToProps
)(Home);
